import React, { useState, useEffect, useMemo } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getUserData } from '../../services/accessControlService';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import {
  FaPlug,
  FaSearch,
  FaCopy,
  FaPlus,
  FaSave,
  FaExternalLinkAlt,
  FaEdit,
  FaUpload,
} from 'react-icons/fa';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Never';
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const maskLicenseKey = (key) => {
  if (!key || key.length < 12) return key || '—';
  return `${key.slice(0, 4)}-****-****-${key.slice(-4)}`;
};

const generateLicenseKey = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const segment = () =>
    Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${segment()}-${segment()}-${segment()}-${segment()}`;
};

const generateApiSecret = () => {
  return Array.from({ length: 32 }, () =>
    'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]
  ).join('');
};

const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text).then(() => {
    alert(`Copied ${label} to clipboard`);
  });
};

const compareVersions = (a, b) => {
  if (!a || !b) return 0;
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na < nb) return -1;
    if (na > nb) return 1;
  }
  return 0;
};

const PluginsFulfillment = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('installations');
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installations, setInstallations] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [releases, setReleases] = useState([]);

  // Search
  const [installSearch, setInstallSearch] = useState('');
  const [licenseSearch, setLicenseSearch] = useState('');

  // Modals
  const [showCreateLicense, setShowCreateLicense] = useState(false);
  const [editingMaxActivations, setEditingMaxActivations] = useState(null);
  const [newLicenseMaxActivations, setNewLicenseMaxActivations] = useState(1);
  const [newLicenseCompanyName, setNewLicenseCompanyName] = useState('');
  const [rotatingApiSecret, setRotatingApiSecret] = useState(new Set());

  // Optimistic toggle state
  const [togglingEnabled, setTogglingEnabled] = useState(new Set());
  const [togglingLicenseStatus, setTogglingLicenseStatus] = useState(new Set());

  const isOwner = userRole === 'Owner';

  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser) return;
      const userData = await getUserData(currentUser.uid);
      if (userData) {
        setUserRole(userData.role);
        if (userData.role !== 'Owner') {
          navigate('/crm/fulfillment');
          return;
        }
      } else {
        navigate('/crm/fulfillment');
      }
    };
    checkAccess();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!isOwner) return;
    fetchData();
  }, [isOwner]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [installsSnap, licensesSnap, releasesSnap] = await Promise.all([
        getDocs(collection(db, 'installations')),
        getDocs(collection(db, 'licenses')),
        getDocs(collection(db, 'releases')),
      ]);

      const installs = installsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const licMap = {};
      licensesSnap.docs.forEach((d) => {
        licMap[d.id] = { id: d.id, ...d.data() };
      });
      const relMap = {};
      releasesSnap.docs.forEach((d) => {
        relMap[d.id] = { id: d.id, ...d.data() };
      });

      // Count active installs per license
      const activeCount = {};
      installs.forEach((i) => {
        if (i.licenseKey) {
          activeCount[i.licenseKey] = (activeCount[i.licenseKey] || 0) + 1;
        }
      });
      Object.keys(licMap).forEach((k) => {
        licMap[k].activeInstallCount = activeCount[k] || 0;
      });

      setInstallations(installs);
      setLicenses(Object.values(licMap));
      setReleases(Object.values(relMap));
    } catch (err) {
      console.error('Error fetching plugin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const releasesBySlug = useMemo(() => {
    const m = {};
    releases.forEach((r) => (m[r.id] = r));
    return m;
  }, [releases]);

  const filteredInstallations = useMemo(() => {
    if (!installSearch.trim()) return installations;
    const q = installSearch.toLowerCase();
    return installations.filter(
      (i) =>
        (i.siteUrl || '').toLowerCase().includes(q) ||
        (i.pluginSlug || '').toLowerCase().includes(q) ||
        (i.licenseKey || '').toLowerCase().includes(q) ||
        (i.companyName || '').toLowerCase().includes(q)
    );
  }, [installations, installSearch]);

  const filteredLicenses = useMemo(() => {
    if (!licenseSearch.trim()) return licenses;
    const q = licenseSearch.toLowerCase();
    return licenses.filter(
      (l) =>
        (l.licenseKey || '').toLowerCase().includes(q) ||
        (l.companyName || '').toLowerCase().includes(q)
    );
  }, [licenses, licenseSearch]);

  const handleToggleEnabled = async (install) => {
    const id = install.id;
    const next = !install.enabled;
    setTogglingEnabled((s) => new Set(s).add(id));
    const prev = install.enabled;
    setInstallations((prevList) =>
      prevList.map((i) => (i.id === id ? { ...i, enabled: next } : i))
    );
    try {
      await updateDoc(doc(db, 'installations', id), { enabled: next });
    } catch (err) {
      console.error(err);
      setInstallations((prevList) =>
        prevList.map((i) => (i.id === id ? { ...i, enabled: prev } : i))
      );
    } finally {
      setTogglingEnabled((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    }
  };

  const handleToggleLicenseStatus = async (license) => {
    const key = license.licenseKey || license.id;
    const next = license.status === 'active' ? 'suspended' : 'active';
    setTogglingLicenseStatus((s) => new Set(s).add(key));
    const prev = license.status;
    setLicenses((prevList) =>
      prevList.map((l) => (l.id === key ? { ...l, status: next } : l))
    );
    try {
      await updateDoc(doc(db, 'licenses', key), { status: next });
    } catch (err) {
      console.error(err);
      setLicenses((prevList) =>
        prevList.map((l) => (l.id === key ? { ...l, status: prev } : l))
      );
    } finally {
      setTogglingLicenseStatus((s) => {
        const n = new Set(s);
        n.delete(key);
        return n;
      });
    }
  };

  const handleCreateLicense = async () => {
    const licenseKey = generateLicenseKey();
    const apiSecret = generateApiSecret();
    try {
      await setDoc(doc(db, 'licenses', licenseKey), {
        licenseKey,
        apiSecret,
        status: 'active',
        maxActivations: parseInt(newLicenseMaxActivations, 10) || 1,
        companyName: newLicenseCompanyName.trim() || null,
      });
      setLicenses((prev) => [
        ...prev,
        {
          id: licenseKey,
          licenseKey,
          apiSecret,
          status: 'active',
          maxActivations: parseInt(newLicenseMaxActivations, 10) || 1,
          companyName: newLicenseCompanyName.trim() || null,
          activeInstallCount: 0,
        },
      ]);
      setShowCreateLicense(false);
      setNewLicenseMaxActivations(1);
      setNewLicenseCompanyName('');
      copyToClipboard(licenseKey, 'License Key');
      copyToClipboard(apiSecret, 'API Secret');
    } catch (err) {
      console.error(err);
      alert('Failed to create license');
    }
  };

  const handleRotateApiSecret = async (license) => {
    const key = license.id;
    setRotatingApiSecret((s) => new Set(s).add(key));
    const newSecret = generateApiSecret();
    const prev = license.apiSecret;
    setLicenses((prevList) =>
      prevList.map((l) => (l.id === key ? { ...l, apiSecret: newSecret } : l))
    );
    try {
      await updateDoc(doc(db, 'licenses', key), { apiSecret: newSecret });
      copyToClipboard(newSecret, 'API Secret');
    } catch (err) {
      console.error(err);
      setLicenses((prevList) =>
        prevList.map((l) => (l.id === key ? { ...l, apiSecret: prev } : l))
      );
    } finally {
      setRotatingApiSecret((s) => {
        const n = new Set(s);
        n.delete(key);
        return n;
      });
    }
  };

  const handleSaveMaxActivations = async (licenseKey, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    const lic = licenses.find((l) => l.id === licenseKey);
    const activeCount = lic?.activeInstallCount ?? 0;
    if (num < activeCount && !window.confirm(`This license has ${activeCount} active installation(s). Setting max to ${num} will block new activations until installations are removed. Continue?`)) {
      return;
    }
    const prev = lic?.maxActivations;
    setLicenses((prevList) =>
      prevList.map((l) => (l.id === licenseKey ? { ...l, maxActivations: num } : l))
    );
    setEditingMaxActivations(null);
    try {
      await updateDoc(doc(db, 'licenses', licenseKey), { maxActivations: num });
    } catch (err) {
      console.error(err);
      setLicenses((prevList) =>
        prevList.map((l) => (l.id === licenseKey ? { ...l, maxActivations: prev } : l))
      );
    }
  };

  const handleSaveRelease = async (pluginSlug, latestVersion, storagePath) => {
    const docRef = doc(db, 'releases', pluginSlug);
    const exists = releases.find((r) => r.id === pluginSlug);
    try {
      await setDoc(docRef, { pluginSlug, latestVersion, storagePath }, { merge: true });
      setReleases((prev) => {
        const rest = prev.filter((r) => r.id !== pluginSlug);
        return [...rest, { id: pluginSlug, pluginSlug, latestVersion, storagePath }];
      });
    } catch (err) {
      console.error(err);
      alert('Failed to save release');
    }
  };

  const isOffline = (install) => {
    const ts = install.lastSeenAt;
    if (!ts) return true;
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    const days = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
    return days > 7;
  };

  const isOutdated = (install) => {
    const release = releasesBySlug[install.pluginSlug];
    if (!release?.latestVersion) return false;
    return compareVersions(install.pluginVersion, release.latestVersion) < 0;
  };

  const getLicenseStatus = (install) => {
    if (!install.licenseKey) return '—';
    const lic = licenses.find((l) => l.id === install.licenseKey);
    if (!lic) return '—';
    return lic.status === 'suspended' ? 'Suspended' : 'Active';
  };

  if (!isOwner && userRole !== null) {
    return null;
  }

  if (loading && installations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="relative flex items-center justify-center mb-6">
          <div className="absolute left-0">
            <BackButton to="/crm/templates" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Plugin Control Center
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          {['installations', 'licenses', 'releases'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize border-b-2 -mb-px transition ${
                activeTab === tab
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Installations */}
        {activeTab === 'installations' && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Installations
              </h2>
              <div className="relative w-64">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by site URL or license key..."
                  value={installSearch}
                  onChange={(e) => setInstallSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-600">
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Company
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Site URL
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Plugin Slug
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Version
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Last Seen
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Enabled
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      License Status
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstallations.map((install) => {
                    const offline = isOffline(install);
                    const outdated = isOutdated(install);
                    const busy = togglingEnabled.has(install.id);
                    return (
                      <tr
                        key={install.id}
                        className={`border-b border-gray-100 dark:border-gray-700 ${
                          offline ? 'bg-amber-50 dark:bg-amber-900/10' : ''
                        }`}
                      >
                        <td className="py-3 px-2 text-gray-700 dark:text-gray-300">
                          {install.companyName || '—'}
                        </td>
                        <td className="py-3 px-2">
                          <a
                            href={install.siteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1"
                          >
                            {install.siteUrl || '—'}
                            <FaExternalLinkAlt className="text-xs" />
                          </a>
                          <div className="flex gap-2 mt-1">
                            {offline && (
                              <span className="text-xs px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200">
                                Offline
                              </span>
                            )}
                            {outdated && (
                              <span className="text-xs px-2 py-0.5 rounded bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200">
                                Outdated
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-gray-700 dark:text-gray-300">
                          {install.pluginSlug || '—'}
                        </td>
                        <td className="py-3 px-2 text-gray-700 dark:text-gray-300">
                          {install.pluginVersion || '—'}
                        </td>
                        <td className="py-3 px-2 text-gray-600 dark:text-gray-400">
                          {formatRelativeTime(install.lastSeenAt)}
                          {install.lastSeenAt?.toDate && (
                            <span className="block text-xs text-gray-500">
                              {install.lastSeenAt.toDate().toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          <button
                            onClick={() => handleToggleEnabled(install)}
                            disabled={busy}
                            className={`relative w-11 h-6 rounded-full transition ${
                              install.enabled
                                ? 'bg-red-500'
                                : 'bg-gray-300 dark:bg-gray-600'
                            } ${busy ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span
                              className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition left-1 ${
                                install.enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </td>
                        <td className="py-3 px-2">
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              getLicenseStatus(install) === 'Active'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            }`}
                          >
                            {getLicenseStatus(install)}
                          </span>
                        </td>
                        <td className="py-3 px-2 flex gap-2">
                          <button
                            onClick={() => copyToClipboard(install.id, 'Install ID')}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                            title="Copy Install ID"
                          >
                            <FaCopy className="text-sm" />
                          </button>
                          {install.licenseKey && (
                            <button
                              onClick={() =>
                                copyToClipboard(install.licenseKey, 'License Key')
                              }
                              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                              title="Copy License Key"
                            >
                              <FaCopy className="text-sm" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredInstallations.length === 0 && (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                No installations found
              </p>
            )}
          </div>
        )}

        {/* Licenses */}
        {activeTab === 'licenses' && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Licenses
              </h2>
              <div className="flex gap-2">
                <div className="relative w-48">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by license key or company..."
                    value={licenseSearch}
                    onChange={(e) => setLicenseSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <button
                  onClick={() => setShowCreateLicense(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  <FaPlus /> Create License
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-600">
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Company
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      License Key
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Status
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Max Activations
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Active Installs
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLicenses.map((lic) => {
                    const busy = togglingLicenseStatus.has(lic.id);
                    const editing = editingMaxActivations === lic.id;
                    const rotating = rotatingApiSecret.has(lic.id);
                    return (
                      <tr
                        key={lic.id}
                        className="border-b border-gray-100 dark:border-gray-700"
                      >
                        <td className="py-3 px-2 text-gray-700 dark:text-gray-300">
                          {lic.companyName || '—'}
                        </td>
                        <td className="py-3 px-2 flex items-center gap-2">
                          <span className="font-mono text-gray-600 dark:text-gray-400">
                            {maskLicenseKey(lic.licenseKey)}
                          </span>
                          <button
                            onClick={() =>
                              copyToClipboard(lic.licenseKey, 'License Key')
                            }
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <FaCopy className="text-sm" />
                          </button>
                        </td>
                        <td className="py-3 px-2">
                          <button
                            onClick={() => handleToggleLicenseStatus(lic)}
                            disabled={busy}
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              lic.status === 'active'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            } ${busy ? 'opacity-50' : 'hover:opacity-80'}`}
                          >
                            {lic.status === 'active' ? 'Active' : 'Suspended'}
                          </button>
                        </td>
                        <td className="py-3 px-2">
                          {editing ? (
                            <input
                              type="number"
                              min="0"
                              defaultValue={lic.maxActivations}
                              onBlur={(e) =>
                                handleSaveMaxActivations(lic.id, e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  handleSaveMaxActivations(
                                    lic.id,
                                    e.target.value
                                  );
                              }}
                              className="w-16 px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => setEditingMaxActivations(lic.id)}
                              className="flex items-center gap-1 text-gray-700 dark:text-gray-300 hover:text-teal-600"
                            >
                              {lic.maxActivations}
                              <FaEdit className="text-xs" />
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-2 text-gray-700 dark:text-gray-300">
                          {lic.activeInstallCount ?? 0}
                        </td>
                        <td className="py-3 px-2 flex gap-2">
                          <button
                            onClick={() => handleRotateApiSecret(lic)}
                            disabled={rotating}
                            className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                            title="Rotate API Secret"
                          >
                            {rotating ? 'Rotating...' : 'Rotate API Secret'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredLicenses.length === 0 && (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                No licenses found
              </p>
            )}
          </div>
        )}

        {/* Releases */}
        {activeTab === 'releases' && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Releases
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Set the current release used by the WordPress updater for each plugin.
            </p>
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm">
              <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Storage path convention:</p>
              <code className="text-gray-600 dark:text-gray-400">
                plugin-releases/&lt;pluginSlug&gt;/&lt;version&gt;/&lt;pluginSlug&gt;.zip
              </code>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Example: <code>plugin-releases/bba-before-after/1.0.1/bba-before-after.zip</code>
              </p>
            </div>
            <UploadReleaseZip
              existingSlugs={releases.map((r) => r.id)}
              onUploadComplete={(slug, version, storagePath) => {
                handleSaveRelease(slug, version, storagePath);
              }}
            />
            <div className="space-y-4 mt-6">
              {releases.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 py-4">
                  No releases yet. Add one below.
                </p>
              ) : (
                releases.map((r) => (
                  <ReleaseRow
                    key={r.id}
                    release={r}
                    onSave={handleSaveRelease}
                  />
                ))
              )}
              <AddReleaseRow
                existingSlugs={releases.map((r) => r.id)}
                onAdd={(slug, version, path) => {
                  handleSaveRelease(slug, version, path);
                }}
              />
            </div>
          </div>
        )}

        {/* Create License Modal */}
        {showCreateLicense && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Create License
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={newLicenseCompanyName}
                    onChange={(e) => setNewLicenseCompanyName(e.target.value)}
                    placeholder="Acme Corp"
                    className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Activations
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newLicenseMaxActivations}
                    onChange={(e) =>
                      setNewLicenseMaxActivations(e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowCreateLicense(false);
                    setNewLicenseMaxActivations(1);
                    setNewLicenseCompanyName('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLicense}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function UploadReleaseZip({ existingSlugs, onUploadComplete }) {
  const [slug, setSlug] = useState('');
  const [version, setVersion] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!slug.trim() || !version.trim() || !file) {
      setError('Plugin slug, version, and ZIP file are required');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError('File must be a .zip');
      return;
    }
    const slugClean = slug.trim().toLowerCase().replace(/\s+/g, '-');
    const storagePath = `plugin-releases/${slugClean}/${version.trim()}/${slugClean}.zip`;
    setError('');
    setUploading(true);
    try {
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      onUploadComplete(slugClean, version.trim(), storagePath);
      setSlug('');
      setVersion('');
      setFile(null);
      if (document.getElementById('plugin-zip-input')) {
        document.getElementById('plugin-zip-input').value = '';
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-6 p-4 border-2 border-dashed border-red-300 dark:border-red-700 rounded-lg bg-red-50/50 dark:bg-red-900/10">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
        <FaUpload /> Upload ZIP for New Release
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Upload a plugin ZIP file. It will be stored at <code>plugin-releases/&lt;slug&gt;/&lt;version&gt;/&lt;slug&gt;.zip</code> and the release will be updated automatically.
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Plugin Slug
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="bba-before-after"
            list="existing-slugs"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          {existingSlugs.length > 0 && (
            <datalist id="existing-slugs">
              {existingSlugs.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
        </div>
        <div className="min-w-[100px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Version
          </label>
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="1.0.1"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div className="min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            ZIP File
          </label>
          <input
            id="plugin-zip-input"
            type="file"
            accept=".zip"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-red-600 file:text-white file:text-sm"
          />
        </div>
        <button
          onClick={handleUpload}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          <FaUpload className="text-sm" /> {uploading ? 'Uploading...' : 'Upload & Save Release'}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

function ReleaseRow({ release, onSave }) {
  const [latestVersion, setLatestVersion] = useState(release.latestVersion || '');
  const [storagePath, setStoragePath] = useState(release.storagePath || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLatestVersion(release.latestVersion || '');
    setStoragePath(release.storagePath || '');
  }, [release]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(release.pluginSlug || release.id, latestVersion, storagePath);
    setSaving(false);
  };

  return (
    <div className="flex flex-wrap items-end gap-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
      <div className="min-w-[140px]">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Plugin Slug
        </label>
        <span className="font-mono text-gray-900 dark:text-white">
          {release.pluginSlug || release.id}
        </span>
      </div>
      <div className="flex-1 min-w-[120px]">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Latest Version
        </label>
        <input
          type="text"
          value={latestVersion}
          onChange={(e) => setLatestVersion(e.target.value)}
          placeholder="1.0.0"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
      <div className="flex-[2] min-w-[200px]">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Storage Path
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={storagePath}
            onChange={(e) => setStoragePath(e.target.value)}
            placeholder="plugin-releases/bba-before-after/1.0.1/bba-before-after.zip"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <button
            type="button"
            onClick={() => setStoragePath(`plugin-releases/${release.pluginSlug || release.id}/${latestVersion || '1.0.0'}/${release.pluginSlug || release.id}.zip`)}
            className="px-2 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap"
            title="Fill from convention"
          >
            Use convention
          </button>
        </div>
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
      >
        <FaSave className="text-sm" /> {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

function AddReleaseRow({ existingSlugs, onAdd }) {
  const [slug, setSlug] = useState('');
  const [version, setVersion] = useState('');
  const [path, setPath] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!slug.trim()) return;
    setSaving(true);
    await onAdd(slug.trim(), version.trim(), path.trim());
    setSlug('');
    setVersion('');
    setPath('');
    setSaving(false);
  };

  return (
    <div className="flex flex-wrap items-end gap-4 p-4 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
      <div className="min-w-[140px]">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          New Plugin Slug
        </label>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="my-plugin"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
      <div className="flex-1 min-w-[120px]">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Latest Version
        </label>
        <input
          type="text"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="1.0.0"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
      <div className="flex-[2] min-w-[200px]">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Storage Path
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="plugin-releases/my-plugin/1.0.0/my-plugin.zip"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <button
            type="button"
            onClick={() => setPath(slug && version ? `plugin-releases/${slug}/${version}/${slug}.zip` : '')}
            className="px-2 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap"
            title="Fill from convention"
          >
            Use convention
          </button>
        </div>
      </div>
      <button
        onClick={handleAdd}
        disabled={saving || !slug.trim()}
        className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
      >
        <FaPlus className="text-sm" /> Add
      </button>
    </div>
  );
}

export default PluginsFulfillment;
